import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { UsageStatus } from "@/lib/api/client";

interface UsageBannerState {
  /**
   * The status at which the banner was last dismissed.
   * Banner reappears if status changes.
   */
  dismissedAtStatus: UsageStatus | null;

  /**
   * Dismiss the quota banner for the current status
   */
  dismissBanner: (status: UsageStatus) => void;

  /**
   * Check if the banner should be shown for a given status
   */
  shouldShowBanner: (status: UsageStatus) => boolean;

  /**
   * Reset dismissed state (for testing or manual reset)
   */
  resetDismissed: () => void;
}

export const useUsageBannerStore = create<UsageBannerState>()(
  persist(
    (set, get) => ({
      dismissedAtStatus: null,

      dismissBanner: (status) => set({ dismissedAtStatus: status }),

      shouldShowBanner: (status) => {
        // Never show banner for "under" status
        if (status === "under") {
          return false;
        }

        const { dismissedAtStatus } = get();

        // If never dismissed, show the banner
        if (dismissedAtStatus === null) {
          return true;
        }

        // Show banner if status has changed since dismissal
        return dismissedAtStatus !== status;
      },

      resetDismissed: () => set({ dismissedAtStatus: null }),
    }),
    {
      name: "usage-banner",
    },
  ),
);
